// Interfaz: orquesta la búsqueda llamando al Worker por lotes y pinta los resultados según llegan.

import { type I18nKey, type Lang, DICTIONARIES, LANGS, num, pctText, t as translate, toLang } from "../core/i18n";
import { type AnalysisResult, ApiError, type AppConfig, type PaperResult } from "../core/models";
import { type Api, type Progress, SearchSession, sourceLabel } from "../core/pipeline";
import type { Correction } from "../core/reliability";
import { withPublicAbstract } from "../core/excerpt";
import { plainSummary } from "../core/summary";
import { MYMEMORY_URL, translateEsEn } from "../core/translate";
import { openalexSearch } from "./openalex";

const $ = (id: string) => document.getElementById(id) as HTMLElement;

let data: AnalysisResult | null = null;
let filter: string | null = null;
let running: AbortController | null = null;
let config: AppConfig | null = null;
let session: SearchSession | null = null;

// ---- idioma (#16): prefijo de la URL (/es/, /en/), si no el elegido antes, si no el del navegador

const LANG_STORAGE = "paperjev-lang";
function initialLang(): Lang {
  const fromPath = location.pathname.match(/^\/(es|en)(\/|$)/)?.[1];
  if (fromPath) return fromPath as Lang;
  try {
    const saved = localStorage.getItem(LANG_STORAGE);
    if (saved === "es" || saved === "en") return saved;
  } catch {
    // sin almacenamiento
  }
  return toLang(navigator.language);
}
let lang: Lang = initialLang();
const t = (key: I18nKey, params?: Record<string, string | number>) => translate(lang, key, params);
// Etiquetas de respuestas y diseños (claves dinámicas; si faltara alguna, la clave cruda)
const label = (kind: "answer" | "design" | "headline" | "certainty", key: string) => {
  const k = `${kind}.${key}` as I18nKey;
  return k in DICTIONARIES[lang] ? t(k) : key;
};

/** Textos fijos de la página (atributos data-i18n) en el idioma actual. */
function applyStaticTexts() {
  document.documentElement.lang = lang;
  document.title = t("page.title");
  document.querySelectorAll<HTMLElement>("[data-i18n]").forEach((el) => (el.textContent = t(el.dataset.i18n as I18nKey)));
  document.querySelectorAll<HTMLInputElement>("[data-i18n-placeholder]").forEach((el) => (el.placeholder = t(el.dataset.i18nPlaceholder as I18nKey)));
  document.querySelectorAll<HTMLElement>("[data-i18n-title]").forEach((el) => (el.title = t(el.dataset.i18nTitle as I18nKey)));
  document.querySelectorAll<HTMLElement>("[data-i18n-aria]").forEach((el) => el.setAttribute("aria-label", t(el.dataset.i18nAria as I18nKey)));
  const other = LANGS.find((l) => l !== lang)!;
  const how = lang === "es" ? "/es/como-funciona" : "/en/how-it-works";
  ($("link-how") as HTMLAnchorElement).href = how;
  ($("link-how-footer") as HTMLAnchorElement).href = how;
  ($("link-privacy") as HTMLAnchorElement).href = lang === "es" ? "/es/privacidad" : "/en/privacy";
  ($("brand") as HTMLAnchorElement).href = `/${lang}/`;
  ($("new-question") as HTMLAnchorElement).href = `/${lang}/`;
  renderExamples();
  const sw = $("lang-switch") as HTMLAnchorElement;
  sw.textContent = t("page.lang_switch");
  sw.href = `/${other}/${location.search}`;
  sw.hreflang = other;
  sw.lang = other;
}

// Cambiar de idioma no repite la búsqueda: se vuelve a pintar lo que hay
$("lang-switch").addEventListener("click", (e) => {
  e.preventDefault();
  lang = LANGS.find((l) => l !== lang)!;
  try {
    localStorage.setItem(LANG_STORAGE, lang);
  } catch {
    // sin almacenamiento
  }
  history.replaceState(null, "", `/${lang}/${location.search}`);
  applyStaticTexts();
  renderBackendNotice();
  renderGallery();
  if (data) render();
});

// ---- clave de acceso de la versión de prueba (?key=… en la URL, se recuerda en el navegador)

const KEY_STORAGE = "paperjev-access-key";
function accessKey(): string {
  const fromUrl = new URLSearchParams(location.search).get("key");
  try {
    if (fromUrl) localStorage.setItem(KEY_STORAGE, fromUrl);
    return fromUrl ?? localStorage.getItem(KEY_STORAGE) ?? "";
  } catch {
    return fromUrl ?? "";
  }
}

// ---- API HTTP del Worker (los mensajes llegan en el idioma de la web)

async function post<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
  const resp = await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json", "x-access-key": accessKey(), "x-lang": lang },
    body: JSON.stringify(body),
    signal,
  });
  const json = (await resp.json().catch(() => ({ detail: `HTTP ${resp.status}` }))) as { detail?: unknown };
  if (!resp.ok) {
    const detail = typeof json.detail === "string" ? json.detail : JSON.stringify(json.detail);
    throw new ApiError(detail, resp.status);
  }
  return json as T;
}

// Usa la señal de la tarea en curso, para que «Parar» corte también las peticiones pendientes
async function get<T>(path: string, signal?: AbortSignal): Promise<T> {
  const resp = await fetch(path, { headers: { "x-access-key": accessKey(), "x-lang": lang }, signal });
  if (!resp.ok) throw new ApiError(`HTTP ${resp.status}`, resp.status);
  return (await resp.json()) as T;
}

const api: Api = {
  stored: (id) => get(`/api/searches/${encodeURIComponent(id)}`, running?.signal),
  plan: (req) => post("/api/plan", req, running?.signal),
  // OpenAlex, desde el navegador con el cupo sin clave de cada persona; si falla, el Worker
  search: (req) =>
    req.source === "openalex"
      ? openalexSearch((input, init) => fetch(input, { ...init, signal: running?.signal }), req, (r) => post("/api/search", r, running?.signal))
      : post("/api/search", req, running?.signal),
  classify: (req) => post("/api/classify", req, running?.signal),
  references: (req) => post("/api/references", req, running?.signal),
  expand: (req) => post("/api/expand", req, running?.signal),
};

// ---- utilidades de pintado

const esc = (s: unknown) =>
  String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
const pct = (x: number | null | undefined) => (x == null ? "–" : Math.round(x * 100) + "%");
const color = (k: string) => `var(--${k})`;

const VERDICT_COLORS: Record<string, string> = {
  yes: "yes", likely_yes: "yes", mixed: "partial", no_evidence: "inconclusive",
  no_effect: "no_effect", likely_no: "no", no: "no", insufficient: "not_addressed",
};
const CERTAINTY_LEVEL: Record<string, number> = { "muy baja": 1, baja: 2, moderada: 3, alta: 4 };

// ---- configuración inicial

function renderBackendNotice() {
  const notice = $("backend-notice");
  if (!config) return;
  if (config.backend_error) {
    notice.innerHTML = `<div class="notice error">${esc(config.backend_error)}</div>`;
  } else if (config.backend === "fake") {
    notice.innerHTML = `<div class="notice">${t("notice.fake")}</div>`;
  }
}

async function loadConfig() {
  try {
    config = (await (await fetch("/api/config")).json()) as AppConfig;
  } catch {
    return;
  }
  // La portada es estática y no pasa por el Worker: la redirección al dominio público (#28) va aquí
  if (config.canonical_host && location.hostname.endsWith(".workers.dev")) {
    location.replace(`https://${config.canonical_host}${location.pathname}${location.search}`);
    return;
  }
  if (config.web_analytics_token) loadAnalytics(config.web_analytics_token);
  renderBackendNotice();
  const limit = document.querySelector<HTMLInputElement>("input[name=limit]")!;
  // La configuración llega tarde: no pisar un valor que el usuario ya ha cambiado
  if (limit.value === limit.defaultValue) limit.value = String(config.default_limit);
  limit.max = String(config.max_limit);
  if (config.turnstile_site_key) loadTurnstile(config.turnstile_site_key);
}

// Cloudflare Web Analytics (#24): cuenta visitas sin cookies ni datos personales
function loadAnalytics(token: string) {
  const script = document.createElement("script");
  script.defer = true;
  script.src = "https://static.cloudflareinsights.com/beacon.min.js";
  script.dataset.cfBeacon = JSON.stringify({ token });
  document.head.appendChild(script);
}

// ---- galería de la portada (#18): preguntas destacadas, con enlace a su página

let gallery: { slug: string; question: string }[] = [];
async function loadGallery() {
  try {
    gallery = (await (await fetch("/api/gallery")).json()) as typeof gallery;
  } catch {
    gallery = [];
  }
  renderGallery();
}
function renderGallery() {
  $("gallery").innerHTML = gallery.length
    ? `<h2>${esc(t("gallery.title"))}</h2><ul>${gallery.map((g) => `<li><a href="/${lang}/q/${esc(g.slug)}">${esc(g.question)}</a></li>`).join("")}</ul>`
    : "";
}

// Preguntas de ejemplo bajo la caja: rellenan la pregunta y la lanzan
const EXAMPLES: I18nKey[] = ["ex.1", "ex.2", "ex.3", "ex.4"];
function renderExamples() {
  $("examples").innerHTML = EXAMPLES.map((k) => `<button type="button" data-q="${esc(t(k))}">${esc(t(k))}</button>`).join("");
  document.querySelectorAll<HTMLButtonElement>("#examples button").forEach((b) =>
    b.addEventListener("click", () => {
      (document.querySelector("input[name=question]") as HTMLInputElement).value = b.dataset.q ?? "";
      startSearch(false);
    }),
  );
}

/** Con resultados, la portada pasa a modo conversación: la caja de la pregunta baja al final. */
function setMode(hasResults: boolean) {
  document.body.classList.toggle("has-results", hasResults);
  $("new-question").hidden = !hasResults;
  $("results").style.display = hasResults ? "flex" : "none";
}

// ---- Turnstile (#14): captcha invisible de Cloudflare; el token se pide al empezar una búsqueda nueva

interface TurnstileApi {
  render(el: HTMLElement, o: Record<string, unknown>): string;
  getResponse(id: string): string | undefined;
  reset(id: string): void;
}
let turnstileId: string | null = null;
// Turnstile avisó de un error (bloqueado, sin conexión…): no tiene sentido esperar un token
let turnstileFailed = false;
// Se resuelve cuando el widget está pintado (el script se carga aparte y puede tardar)
let turnstileReady: Promise<void> | null = null;

function loadTurnstile(siteKey: string) {
  let ready!: () => void;
  turnstileReady = new Promise<void>((resolve) => (ready = resolve));
  (window as unknown as { onTurnstileLoad: () => void }).onTurnstileLoad = () => {
    const ts = (window as unknown as { turnstile: TurnstileApi }).turnstile;
    turnstileId = ts.render($("turnstile"), {
      sitekey: siteKey,
      appearance: "interaction-only",
      action: "search",
      language: lang,
      "error-callback": () => {
        turnstileFailed = true;
      },
      "unsupported-callback": () => {
        turnstileFailed = true;
      },
      callback: () => {
        turnstileFailed = false;
      },
    });
    ready();
  };
  const script = document.createElement("script");
  script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=onTurnstileLoad";
  script.async = true;
  document.head.appendChild(script);
}

// Un token vale para una sola búsqueda: se entrega y se pide otro. Si aún no está listo (se
// acaba de cargar la página o de usar el anterior), se espera unos segundos
async function turnstileToken(): Promise<string | undefined> {
  if (!turnstileReady) return undefined;
  await Promise.race([turnstileReady, new Promise((r) => setTimeout(r, 10_000))]);
  if (turnstileId === null) return undefined;
  const ts = (window as unknown as { turnstile: TurnstileApi }).turnstile;
  let token = ts.getResponse(turnstileId);
  for (let i = 0; !token && !turnstileFailed && i < 50; i++) {
    await new Promise((r) => setTimeout(r, 200));
    token = ts.getResponse(turnstileId);
  }
  ts.reset(turnstileId);
  return token;
}

// ---- búsqueda

function showProgress(p: Progress) {
  const params = { classified: p.classified, found: p.found };
  $("spinner").textContent =
    p.stage === "planning" ? t("progress.planning")
    : p.stage === "searching" ? t("progress.searching")
    : p.stage === "classifying" ? t("progress.classifying", params)
    : p.stage === "exhaustive" ? t("progress.exhaustive", params)
    : "";
}

function setBusy(busy: boolean) {
  ($("submit") as HTMLButtonElement).disabled = busy;
  $("stop").hidden = !busy;
  $("spinner").style.display = busy ? "inline-flex" : "none";
  renderMore();
  renderShare();
}

/** Ejecuta una fase de la búsqueda; «Parar» o una nueva búsqueda la cancelan. */
async function run(task: (signal: AbortSignal) => Promise<AnalysisResult>) {
  running?.abort();
  const controller = new AbortController();
  running = controller;
  setBusy(true);
  $("error").style.display = "none";
  try {
    data = await task(controller.signal);
    render();
  } catch (err) {
    if ((err as Error).name === "AbortError") {
      // Se conservan los resultados parciales
      if (running === controller && data) render();
      return;
    }
    setMode(false);
    $("error").textContent = (err as Error).message;
    $("error").style.display = "block";
  } finally {
    if (running === controller) {
      running = null;
      setBusy(false);
    }
  }
}

const onUpdate = (controller: AbortController) => (partial: AnalysisResult) => {
  if (running !== controller) return;
  data = partial;
  render();
};

$("form").addEventListener("submit", (e) => {
  e.preventDefault();
  // Las opciones se pliegan al buscar: la caja vuelve a ocupar una línea
  ($("options") as HTMLDetailsElement).open = false;
  startSearch(false);
});

// refresh: repetir la búsqueda aunque haya una guardada reciente («Actualizar»)
function startSearch(refresh: boolean) {
  const f = new FormData($("form") as HTMLFormElement);
  filter = null;
  data = null;
  run((signal) => {
    const controller = running!;
    session = new SearchSession(api, {
      question: String(f.get("question") ?? ""),
      query: String(f.get("query") ?? ""),
      limit: Math.min(Number(f.get("limit")) || 100, config?.max_limit ?? 200),
      titleAbsOnly: f.get("title_abs_only") === "on",
      backend: config?.backend ?? "?",
      batchSize: config?.batch_size,
      pageSize: config?.page_size,
      sources: config?.sources,
      signal,
      // MyMemory desde el navegador: usa la IP y la cuota gratuita de cada usuario
      translate: (q) => translateEsEn(fetch.bind(window), MYMEMORY_URL, q, "", 8_000),
      onProgress: showProgress,
      onUpdate: onUpdate(controller),
      refresh,
      turnstileToken,
      lang,
    });
    return session.quick();
  });
}

function exhaustive() {
  const s = session;
  if (!s) return;
  run((signal) =>
    s.exhaustive({
      max: config?.exhaustive_max ?? 1000,
      signal,
      onProgress: showProgress,
      onUpdate: onUpdate(running!),
    }),
  );
}

$("stop").addEventListener("click", () => running?.abort());
// Cerrar o abandonar la página corta la búsqueda: no se sigue gastando
addEventListener("pagehide", () => running?.abort());

function renderMore() {
  const more = $("more");
  const n = data?.results.length ?? 0;
  if (!session || !data || !n || running) {
    more.innerHTML = "";
    return;
  }
  const max = config?.exhaustive_max ?? 1000;
  // Resultado de la caché: se puede repetir la búsqueda desde cero
  const refresh = data.cached ? `<button type="button" id="refresh" class="btn">${esc(t("more.refresh"))}</button>` : "";
  if (data.exhaustive_done) {
    more.innerHTML = `<div class="more"><span>${esc(t("more.done", { n }))}</span></div>`;
  } else if (data.limit_reached || n >= max) {
    more.innerHTML = `<div class="more"><span>${esc(t("more.max", { n }))}</span></div>`;
  } else if (session.remaining > 0) {
    const next = Math.min(session.remaining, max - n);
    more.innerHTML = `<div class="more"><span>${esc(t("more.next", { next: num(lang, next), total: num(lang, data.total_found) }))}</span>
      <button type="button" id="exhaustive" class="btn">${esc(t("more.continue"))}</button></div>`;
    $("exhaustive").addEventListener("click", exhaustive);
  } else {
    more.innerHTML = data.cached ? `<div class="more"><span>${esc(t("more.cached_done", { n }))}</span></div>` : "";
  }
  if (refresh) {
    more.insertAdjacentHTML("beforeend", `<div class="more"><span>${esc(t("more.refresh_help"))}</span>${refresh}</div>`);
    $("refresh").addEventListener("click", () => startSearch(true));
  }
}

function explanation() {
  const v = data!.verdict!;
  if (v.label === "insufficient") return t("explanation.insufficient", { n: v.n_relevant });
  return t("explanation.main", {
    n: v.n_relevant,
    strong: v.n_strong ?? "?",
    support: pctText(v.shares.support),
    against: pctText(v.shares.against),
    neutral: pctText(v.shares.neutral),
  });
}

const ICON_LINK = `<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1"></path><path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1"></path></svg>`;
const ICON_SHARE = `<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 15V3M7 8l5-5 5 5M5 13v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6"></path></svg>`;
const ICON_CHECK = `<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12l5 5L20 7"></path></svg>`;

/** Barra con el peso a favor / en contra / no concluyente del veredicto y su leyenda. */
function sharesHtml() {
  const s = data!.verdict!.shares;
  const parts: [string, number, I18nKey][] = [
    ["yes", s.support, "verdict.support"],
    ["no", s.against, "verdict.against"],
    ["inconclusive", s.neutral, "verdict.neutral"],
  ];
  const shown = parts.filter(([, x]) => x > 0);
  if (!shown.length) return "";
  const label = shown.map(([, x, k]) => t(k, { pct: pctText(x) })).join(", ");
  return `<div class="vbar" role="img" aria-label="${esc(label)}">${shown
    .map(([c, x]) => `<span style="width:${(100 * x).toFixed(1)}%;background:${color(c)}"></span>`)
    .join("")}</div>
    <div class="vlegend">${shown.map(([c, x, k]) => `<span><span class="dot" style="background:${color(c)}"></span>${esc(t(k, { pct: pctText(x) }))}</span>`).join("")}</div>`;
}

function renderVerdict() {
  const v = data!.verdict;
  if (!v) {
    $("verdict").innerHTML = "";
    return;
  }
  const level = CERTAINTY_LEVEL[v.certainty] ?? 1;
  const dots = [1, 2, 3, 4].map((i) => `<i class="${i <= level ? "on" : ""}"></i>`).join("");
  const score = `${v.score >= 0 ? "+" : ""}${v.score.toFixed(2)}`;
  $("verdict").innerHTML = `
    <div class="verdict">
      <div class="kicker">${esc(t("verdict.kicker"))}</div>
      <div class="vhead">
        <h2>${esc(label("headline", v.label))}</h2>
        <span class="certainty ${level >= 3 ? "high" : ""}">${dots} ${esc(t("verdict.certainty"))} <b>${esc(label("certainty", v.certainty))}</b></span>
      </div>
      ${sharesHtml()}
      ${summaryHtml()}
      ${data!.human_focus !== false ? `<p class="health">${esc(t("disclaimer.health"))}</p>` : ""}
      <div class="actions">
        <span id="share"></span>
        <details><summary>${esc(t("verdict.how"))}</summary>
          <div class="how">
            <p>${esc(explanation())}</p>
            <ul>
              <li>${esc(t("how.weights"))}</li>
              <li>${esc(t("how.headline", { score }))}</li>
              <li>${esc(t("how.corrections"))}</li>
              <li>${esc(t("how.certainty"))}</li>
              <li>${esc(t("how.disclaimer"))}</li>
            </ul>
          </div>
        </details>
      </div>
    </div>`;
}

/** Texto de una corrección de fiabilidad en el idioma actual (con sus datos, si los trae). */
function correctionText(k: Correction): string {
  const p = k.params;
  if (!p) return k.text;
  if (k.kind === "retracted") return Number(p.n) === 1 ? t("corr.retracted_one") : t("corr.retracted_many", p);
  if (k.kind === "author_group") return t("corr.author_group", p);
  return t("corr.tiebreak", p);
}

// Correcciones de fiabilidad aplicadas al veredicto (retractados, grupos de autores, revisiones)
function renderCorrections() {
  const cs = data!.verdict?.corrections ?? [];
  if (!cs.length) {
    $("corrections").innerHTML = "";
    return;
  }
  const items = cs.filter((k) => k.kind !== "author_group").map((k) => `<li>${esc(correctionText(k))}</li>`);
  // Los grupos de autores pueden ser muchos: una línea y el detalle desplegable, del mayor al menor
  const groups = cs.filter((k) => k.kind === "author_group").sort((a, b) => b.papers.length - a.papers.length);
  if (groups.length) {
    const n = groups.reduce((a, k) => a + k.papers.length, 0);
    const head = groups.length === 1 ? t("corr.groups_one", { n }) : t("corr.groups", { n, groups: groups.length });
    items.push(`<li><details><summary>${esc(head)}</summary>
      <ul style="margin-top:8px">${groups.map((k) => `<li>${esc(correctionText(k))}</li>`).join("")}</ul></details></li>`);
  }
  $("corrections").innerHTML = `<details class="fold"><summary>${esc(t("corr.title"))}<span class="count">${cs.length}</span></summary>
    <div class="fold-body"><ul>${items.join("")}</ul></div></details>`;
}

// Factor de fiabilidad de cada paper (1 si no hay corrección)
function reliabilityFactor(id: string) {
  let f = 1;
  for (const k of data?.verdict?.corrections ?? []) if (k.kind !== "reviews_tiebreak" && k.papers.includes(id)) f *= k.factor;
  return f;
}

// Resumen en lenguaje llano (en el idioma de la web): cada [n] es una píldora que enlaza al paper
function summaryHtml() {
  const s = plainSummary(data!.verdict, data!.results, lang, data!.human_focus ?? true);
  if (!s) return `<p class="plain">${esc(explanation())}</p>`;
  const byN = new Map(s.citations.map((c) => [c.n, c]));
  const text = esc(s.text).replace(/\s?\[(\d+)\]/g, (m, n: string) => {
    const c = byN.get(Number(n));
    if (!c) return m;
    return c.url
      ? ` <a class="cite" href="${esc(c.url)}" target="_blank" rel="noopener" title="${esc(c.title)}">${n}</a>`
      : ` <span class="cite" title="${esc(c.title)}">${n}</span>`;
  });
  const refs = s.citations
    .map((c) => `<li>${c.url ? `<a href="${esc(c.url)}" target="_blank" rel="noopener">${esc(c.title)}</a>` : esc(c.title)}${c.year ? ` <span class="src">${c.year}</span>` : ""}</li>`)
    .join("");
  return `<p class="plain">${text}</p>${refs ? `<ol class="refs">${refs}</ol>` : ""}`;
}

function renderReviews() {
  const v = data!.verdict;
  const reviews = data!.results
    .filter((r) => r.classification?.design === "meta_analysis" && r.classification.answer !== "not_addressed")
    .slice(0, 6);
  if (!v) {
    $("reviews").innerHTML = "";
    return;
  }
  if (!reviews.length) {
    $("reviews").innerHTML = `<details class="fold"><summary>${esc(t("reviews.title"))}<span class="count">0</span></summary>
      <div class="fold-body"><span class="src">${esc(t("reviews.none"))}</span></div></details>`;
    return;
  }
  const counts = Object.entries(v.reviews.counts)
    .filter(([, c]) => c > 0)
    .map(([k, c]) => `<span><span class="dot" style="background:${color(k)}"></span> ${esc(label("answer", k))} ${c}</span>`)
    .join("");
  $("reviews").innerHTML = `<details class="fold"><summary>${esc(t("reviews.title"))}<span class="count">${v.reviews.total}</span></summary>
    <div class="fold-body">
    <div class="counts">${counts}</div>
    <ul>${reviews
      .map((r) => {
        const p = r.paper;
        const c = r.classification!;
        const title = p.url ? `<a href="${esc(p.url)}" target="_blank" rel="noopener">${esc(p.title)}</a>` : esc(p.title);
        return `<li><span class="dot" style="background:${color(c.answer)}" title="${esc(label("answer", c.answer))}"></span><span>${title} <span class="src">${esc(label("answer", c.answer))} · ${esc(p.year ?? "")}</span></span></li>`;
      })
      .join("")}</ul></div></details>`;
}

function render() {
  if (!data) return;
  $("bubble").innerHTML = data.question_en && data.question_en !== data.question
    ? `${esc(data.question)}<span class="src">${esc(data.question_en)}</span>`
    : esc(data.question);
  renderVerdict();
  renderReviews();
  if (data.verdict) renderCorrections();
  else $("corrections").innerHTML = "";
  $("notices").innerHTML = data.notices.map((n) => `<div>${esc(n)}</div>`).join("");
  const n = data.results.length;
  const errors = data.results.filter((r) => r.error).length;
  $("meta-short").textContent = t("meta.analyzed", { n, total: num(lang, data.total_found) });
  $("meta").innerHTML =
    `${esc(t("meta.analyzed", { n, total: num(lang, data.total_found) }))} · ` +
    `${esc(t("meta.query"))}: <code>${esc(data.query)}</code>` +
    (data.backend !== "jev" ? ` · backend: <b>${esc(data.backend)}</b>` : "") +
    (errors ? ` · <b>${esc(t("meta.errors", { n: errors }))}</b>` : "");

  const classified = Object.values(data.summary).reduce((a, b) => a + b, 0) || 1;
  $("bar").innerHTML = Object.entries(data.summary)
    .filter(([, c]) => c > 0)
    .map(([k, c]) => `<span title="${esc(label("answer", k))}: ${c}" style="width:${(100 * c) / classified}%;background:${color(k)}"></span>`)
    .join("");
  $("legend").innerHTML = Object.entries(data.summary)
    .filter(([, c]) => c > 0)
    .map(
      ([k, c]) => `<button type="button" class="chip ${filter === k ? "active" : ""}" data-k="${k}" aria-pressed="${filter === k}">
        <span class="dot" style="background:${color(k)}"></span>${esc(label("answer", k))} <b>${c}</b></button>`,
    )
    .join("");
  document.querySelectorAll<HTMLElement>("#legend .chip").forEach((el) =>
    el.addEventListener("click", () => {
      filter = filter === el.dataset.k ? null : (el.dataset.k ?? null);
      render();
    }),
  );

  $("rows").innerHTML = data.results
    .filter((r) => !filter || r.classification?.answer === filter)
    .map(row)
    .join("");
  document.querySelectorAll<HTMLButtonElement>("#rows button.report").forEach((b) => b.addEventListener("click", () => openReport(b)));
  renderMore();
  renderShare();
  setMode(true);
}

// Enlace a la página propia de la pregunta (#17): copiar o compartir (Web Share API en móvil)
function renderShare() {
  const box = document.getElementById("share");
  if (!box) return;
  if (!data?.slug || running) {
    box.innerHTML = "";
    return;
  }
  const url = `${location.origin}/${lang}/q/${data.slug}`;
  const native = typeof navigator.share === "function";
  box.innerHTML = `<input id="share-url" readonly value="${esc(url)}" aria-label="${esc(t("share.link"))}" tabindex="-1">
    <button type="button" class="icon-btn" id="share-copy" title="${esc(t("share.copy"))}" aria-label="${esc(t("share.copy"))}">${ICON_LINK}</button>
    ${native ? `<button type="button" class="icon-btn" id="share-native" title="${esc(t("share.native"))}" aria-label="${esc(t("share.native"))}">${ICON_SHARE}</button>` : ""}`;
  $("share-copy").addEventListener("click", async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      ($("share-url") as HTMLInputElement).select();
      document.execCommand("copy");
    }
    $("share-copy").innerHTML = ICON_CHECK;
    $("share-copy").title = t("share.copied");
    $("share-copy").setAttribute("aria-label", t("share.copied"));
  });
  if (native) {
    $("share-native").addEventListener("click", () => {
      navigator.share({ title: data!.question, url }).catch(() => undefined);
    });
  }
}

const REPORT_OPTIONS = ["yes", "partial", "no_effect", "no", "inconclusive"] as const;

/** Formulario en línea para reportar una clasificación (#19). */
function openReport(button: HTMLButtonElement) {
  const r = data?.results.find((x) => x.paper.source_id === button.dataset.id);
  if (!r || !data?.search_id) return;
  const cell = button.closest(".paper")!;
  const options = [...REPORT_OPTIONS.filter((k) => k !== r.classification?.answer).map((k) => [k, label("answer", k)]), ["not_relevant", t("fb.not_relevant")]];
  const form = document.createElement("form");
  form.className = "report-form";
  form.innerHTML = `<label>${esc(t("fb.question"))}<select name="suggested">${options.map(([k, l]) => `<option value="${k}">${esc(l)}</option>`).join("")}</select></label>
    <label>${esc(t("fb.comment"))}<textarea name="comment" maxlength="500" rows="2"></textarea></label>
    <div class="row"><button type="button" class="btn cancel">${esc(t("fb.cancel"))}</button> <button type="submit" class="btn primary">${esc(t("fb.send"))}</button></div>`;
  button.hidden = true;
  cell.appendChild(form);
  form.querySelector(".cancel")!.addEventListener("click", () => {
    form.remove();
    button.hidden = false;
  });
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const f = new FormData(form);
    try {
      await post("/api/feedback", {
        search_id: data!.search_id,
        suggested: f.get("suggested"),
        comment: String(f.get("comment") ?? "").trim() || undefined,
        paper: r.paper,
      });
      form.outerHTML = `<div class="src">${esc(t("fb.thanks"))}</div>`;
    } catch (err) {
      form.insertAdjacentHTML("beforeend", `<div class="src" style="color:var(--no)">${esc((err as Error).message)}</div>`);
    }
  });
}

function weightNote(id: string) {
  const f = reliabilityFactor(id);
  if (f === 1) return "";
  return f === 0
    ? ` · <span title="${esc(t("row.retracted_title"))}">${esc(t("row.retracted"))}</span>`
    : ` · <span title="${esc(t("row.group_title", { f: f.toFixed(2) }))}">${esc(t("row.group", { f: f.toFixed(2) }))}</span>`;
}

function row(r: PaperResult) {
  const p = r.paper;
  const c = r.classification;
  const title = p.url
    ? `<a class="p-title" href="${esc(p.url)}" target="_blank" rel="noopener">${esc(p.title)}</a>`
    : `<span class="p-title">${esc(p.title)}</span>`;
  const where = (p.sources?.length ? p.sources : p.source ? [p.source] : []).map(sourceLabel).join(" + ");
  const src = [p.authors, p.journal, p.year, where, p.via].filter(Boolean).map(esc).join(" · ");
  // Versión legal gratuita (acceso abierto, #56)
  const free = p.oa_url ? `<a class="oa" href="${esc(p.oa_url)}" target="_blank" rel="noopener">${esc(t("row.free"))} ↗</a>` : "";
  // Abstract completo solo con licencia abierta; si no, el extracto y el enlace al original (#21)
  const shown = withPublicAbstract(p);
  const abstract = shown.abstract_is_excerpt
    ? `<p class="excerpt" title="${esc(t("row.excerpt_note"))}">«${esc(shown.abstract)}»${p.url ? ` <a href="${esc(p.url)}" target="_blank" rel="noopener">${esc(t("row.read_source"))}</a>` : ""}</p>`
    : `<details><summary>${esc(t("row.abstract"))}</summary><p>${esc(p.abstract)}</p></details>`;
  if (!c) {
    return `<tr><td><div class="paper">
      <div class="p-meta"><span class="dot" style="background:var(--no)"></span>${esc(t("row.error"))}</div>
      ${title}<div class="src">${esc(r.error)}</div></div></td></tr>`;
  }
  const raw = c.answer !== c.raw_answer ? t("row.model_said", { answer: label("answer", c.raw_answer) }) : "";
  const answer = `<span class="${c.uncertain ? "unc" : ""}" title="${c.uncertain ? esc(t("row.uncertain")) : ""}${esc(raw)}">${esc(label("answer", c.answer))}${c.uncertain ? " ?" : ""}</span>`;
  const humans = c.humans < 0.5 ? t("row.non_human") : "";
  const numbers = `${esc(t("th.relevance"))} ${pct(c.relevance)} · ${esc(t("th.confidence"))} ${pct(c.answer_confidence)}`;
  const weight = c.weight > 0 ? ` · <span title="${esc(t("th.weight_title"))}">${esc(t("th.weight"))} ${c.weight.toFixed(2)}</span>` : "";
  // «¿Mal clasificado?» (#19): solo si la búsqueda está guardada
  const report = data?.search_id ? `<button type="button" class="link-btn report" data-id="${esc(p.source_id)}">${esc(t("fb.open"))}</button>` : "";
  return `<tr><td><article class="paper">
    <div class="p-meta"><span class="dot" style="background:${color(c.answer)}"></span>${answer} · ${esc(label("design", c.design))}${esc(humans)}${weight}${weightNote(p.source_id)}</div>
    ${title}
    <div class="src">${src}</div>
    ${abstract}
    <div class="p-actions">${free}<span class="src">${numbers}</span>${report}</div>
  </article></td></tr>`;
}

$("download").addEventListener("click", () => {
  // La descarga tampoco lleva abstracts completos con licencia no abierta (#21)
  const exported = data && { ...data, results: data.results.map((r) => ({ ...r, paper: withPublicAbstract(r.paper) })) };
  const blob = new Blob([JSON.stringify(exported, null, 2)], { type: "application/json" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = "quedicelaciencia-" + new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-") + ".json";
  a.click();
  URL.revokeObjectURL(a.href);
});

applyStaticTexts();
loadGallery();
// ?q=… (desde la página de una pregunta): se rellena y se lanza; si está guardada, sale de la caché
const initialQuestion = new URLSearchParams(location.search).get("q");
loadConfig().then(() => {
  if (!initialQuestion) return;
  (document.querySelector("input[name=question]") as HTMLInputElement).value = initialQuestion;
  startSearch(false);
});
